import { axiosPrivate } from '@api/ApiHelper/axiosPrivate';
import { PurchaseInventoryState } from '@api/CashFlows/CashFlow';
import { InventoryMovementItem, ShrinkageReason } from '@api/Inventory/InventoryMovement';
import { can, Capability } from '@utils/Permissions/Capabilities';
import { getCurrentPermissionSubject } from '@utils/Permissions';

// ============================================================================
// Cliente del motor de inventario (F5). La lógica de explosión vive SOLO en el
// server (krispis-server, `src/api/Inventory/`): aquí únicamente se dispara.
//
// Por qué en el server y no aquí: es la única copia de la explosión y de ella
// depende todo el stock. Duplicarla en admin y web nos habría dejado dos verdades
// (el error que ya pagamos con los puntos, que hoy son réplicas exactas a mano).
//
// Todo es IDEMPOTENTE por `referenceId`: reintentar no duplica. Por eso los
// fallos NO se propagan a la UI — la venta ya ocurrió y no se puede deshacer
// porque el inventario no respondiera; se reintenta después.
// ============================================================================

export interface CommitInventoryResult {
    status: 'committed' | 'already-committed' | 'nothing-to-commit' | 'disabled';
    movementId?: string;
    warnings: string[];
}

export default {
    /**
     * Iguala el inventario a lo que la venta dice hoy: la primera vez descuenta todo,
     * después solo la diferencia, y si nada cambió el server no escribe nada. Por eso
     * se llama al crear, al cobrar y al completar sin contar cuántas veces pasó. Un
     * pedido que espera pago no descuenta (el server lo rechaza).
     * Nunca lanza: la venta manda. Devuelve undefined si no se pudo.
     */
    async commitOrder(orderId: string, userId?: string): Promise<CommitInventoryResult | undefined> {
        try {
            const response = await axiosPrivate.post(`/inventory/orders/${orderId}/commit`, { userId });
            return response.data as CommitInventoryResult;
        } catch (error) {
            // El server escala a cero: un primer request puede tardar o fallar.
            // El endpoint es idempotente, así que reintentar luego es seguro.
            console.error('No se pudo descontar el inventario de la orden', orderId, error);
            return undefined;
        }
    },

    /** Devuelve al inventario lo que consumió una orden cancelada o borrada. */
    async revertOrder(orderId: string, userId?: string): Promise<CommitInventoryResult | undefined> {
        try {
            const response = await axiosPrivate.post(`/inventory/orders/${orderId}/revert`, { userId });
            return response.data as CommitInventoryResult;
        } catch (error) {
            console.error('No se pudo revertir el inventario de la orden', orderId, error);
            return undefined;
        }
    },

    /**
     * Registra un CONTEO FÍSICO de la sucursal. Fija el saldo a lo contado y deja
     * en el ledger la diferencia contra lo que el sistema creía — es el paso que
     * habilita encender el descuento (§4.2) y el checkpoint del rebuild.
     * A diferencia de commit/revert, este SÍ propaga el error: el usuario está
     * capturando y tiene que saber si no se guardó.
     */
    async registerCount(
        branchOfficeId: string,
        items: { inputId: string; countedQuantity: number }[],
        options: { userId?: string; note?: string } = {}
    ): Promise<{ movementId: string; discrepancies: number; totalShrinkage: number }> {
        const response = await axiosPrivate.post(`/inventory/branch/${branchOfficeId}/count`, {
            items,
            userId: options.userId,
            note: options.note
        });
        return response.data;
    },

    /**
     * MERMA (plan de inventario F2): lo que se tiró, caducó o se echó a perder sale del
     * inventario con motivo de la lista, nota, quién capturó y quién autorizó con su PIN.
     * 🚨 **El gate va aquí, sobre la función, no sobre el botón** (`shrinkagePath.test.ts`
     * lee el fuente): la capacidad en la sucursal y el PIN de quien autoriza. PROPAGA el
     * error: quien captura tiene que saber si no se guardó.
     */
    async registerShrinkage(
        branchOfficeId: string,
        items: { inputId: string; quantity: number }[],
        options: { reason: ShrinkageReason; note?: string; userId?: string; authorizedBy?: string }
    ): Promise<{ movementId: string; items: InventoryMovementItem[]; totalAmount: number; warnings: string[] }> {
        if (!can(getCurrentPermissionSubject(), Capability.InventoryShrinkage, { branchOfficeId })) {
            throw new Error('Tu perfil no puede registrar merma en esta sucursal');
        }
        if (!options?.authorizedBy) {
            throw new Error('La merma la autoriza con su PIN un gerente, admin de sucursal o admin');
        }
        const response = await axiosPrivate.post(`/inventory/branch/${branchOfficeId}/shrinkage`, {
            items,
            reason: options.reason,
            note: options.note,
            userId: options.userId,
            authorizedBy: options.authorizedBy
        });
        return response.data;
    },

    /** Ajuste manual (corrección del admin). El motivo es obligatorio. Sin pantalla. */
    async registerAdjustment(
        branchOfficeId: string,
        items: { inputId: string; quantity: number }[],
        options: { userId?: string; note: string }
    ): Promise<{ movementId: string }> {
        const response = await axiosPrivate.post(`/inventory/branch/${branchOfficeId}/adjustment`, {
            items,
            userId: options.userId,
            note: options.note
        });
        return response.data;
    },

    /**
     * Iguala el ledger a un gasto de "Compra de insumo" (plan de inventario F1): el
     * mismo endpoint sirve para crear, corregir y cancelar, y repetirlo es inofensivo.
     * PROPAGA el error: quien llama (`ExpenseServ.syncPurchaseInventory`) decide que el
     * gasto se queda pendiente y lo dice; el aseo lo reintenta.
     */
    async syncPurchase(
        cashFlowId: string,
        userId?: string
    ): Promise<{
        status: 'synced' | 'already-synced' | 'nothing-to-sync';
        movementId?: string;
        items: { inputId: string; inputName: string; quantity: number; unit: string }[];
        inventory?: PurchaseInventoryState;
        warnings: string[];
    }> {
        const response = await axiosPrivate.post(`/inventory/purchases/${cashFlowId}/sync`, { userId });
        return response.data;
    },

    /**
     * Recibe un pedido de insumos del CEDIS a través del ledger (F5 ③).
     * Reemplaza a `BranchOfficeInventoryServ.saveInventoryFromOrder`, que no era
     * idempotente (recibir dos veces sumaba dos veces), no esperaba sus escrituras,
     * se saltaba la recepción en silencio si el CEDIS no tenía existencia, y no
     * actualizaba `totalUnits`. Propaga el error: quien recibe debe saber si falló.
     */
    async receiveInventoryOrder(
        inventoryOrderId: string,
        userId?: string
    ): Promise<{ status: 'received' | 'already-received'; warnings: string[] }> {
        const response = await axiosPrivate.post(
            `/inventory/inventory-orders/${inventoryOrderId}/receive`,
            { userId }
        );
        return response.data;
    },

    /**
     * Reconstruye el saldo de la sucursal replicando su ledger desde el último
     * conteo físico. `dryRun` calcula sin escribir, para revisar antes de aplicar.
     */
    async rebuildBranchInventory(
        branchOfficeId: string,
        dryRun?: boolean
    ): Promise<{
        applied: boolean;
        balances: {
            inputId: string;
            inputName: string;
            unit: string;
            totalUnits: number;
            previousTotalUnits: number;
            difference: number;
        }[];
    }> {
        const response = await axiosPrivate.post(
            `/inventory/branch/${branchOfficeId}/rebuild${dryRun ? '?dryRun=true' : ''}`,
            {}
        );
        return response.data;
    }
};
